// THE CROW — the field's 10-second threat. Now and then (CrowConfig) a crow's shadow sweeps in from the field's edge and the bird
// lands on a row that has rice on it. It pecks for sitMs; CLICK it before then and it flies off in a burst of feathers (the row is
// safe); leave it and it EATS a notch — CrowConfig.eatsClicks of this season's clicks are lost (seasonManager.loseClicks: the row
// visibly shrinks and the harvest is poorer). One crow at a time, never during a raid, only on the Play page. Sounds and the
// log lines are loopFeedback's (CrowLand / CrowShooed / CrowAte); the click comes through fieldClicks.js.
import * as THREE from 'three'
import gsap from 'gsap'
import { Events, emit } from '../ui/eventBus.js'
import { CrowConfig } from '../config/balance.js'
import * as season from '../game/seasonManager.js'

const FEATHER = [0.86, 0.86, 0.92]

function buildCrow() {
  const g = new THREE.Group(); g.name = 'crow'
  const dark = new THREE.MeshLambertMaterial({ color: 0x17171c }), beakM = new THREE.MeshLambertMaterial({ color: 0x8a6a2a }), eyeM = new THREE.MeshBasicMaterial({ color: 0xe8e8e8 })
  const body = new THREE.Mesh(new THREE.SphereGeometry(0.055, 10, 8), dark); body.scale.set(1, 0.75, 1.45); body.position.y = 0.055
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.03, 10, 8), dark); head.position.set(0, 0.1, 0.07)
  const beak = new THREE.Mesh(new THREE.ConeGeometry(0.011, 0.04, 6), beakM); beak.rotation.x = Math.PI / 2; beak.position.set(0, 0.096, 0.115)
  const eyeL = new THREE.Mesh(new THREE.SphereGeometry(0.005, 6, 6), eyeM); eyeL.position.set(0.014, 0.106, 0.09)
  const eyeR = eyeL.clone(); eyeR.position.x = -0.014
  const tail = new THREE.Mesh(new THREE.BoxGeometry(0.035, 0.008, 0.06), dark); tail.position.set(0, 0.06, -0.09); tail.rotation.x = -0.35
  const wingGeo = new THREE.PlaneGeometry(0.1, 0.05)
  const wingL = new THREE.Mesh(wingGeo, new THREE.MeshLambertMaterial({ color: 0x17171c, side: THREE.DoubleSide })); wingL.position.set(0.055, 0.07, 0); wingL.rotation.z = 0.2
  const wingR = wingL.clone(); wingR.position.x = -0.055; wingR.rotation.z = -0.2
  g.add(body, head, beak, eyeL, eyeR, tail, wingL, wingR)
  g.userData.parts = { head, wingL, wingR, headY: head.position.y }
  return g
}

export function createCrow({ scene, rice, fx, canSpawn = () => true }) {
  const crow = buildCrow(); crow.visible = false; scene.add(crow)
  const shadow = new THREE.Mesh(new THREE.CircleGeometry(0.075, 18), new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.32, depthWrite: false }))
  shadow.rotation.x = -Math.PI / 2; shadow.visible = false; shadow.raycast = () => {}; shadow.name = 'crow-shadow'; scene.add(shadow)
  let state = 'idle', row = null, sitUntil = 0, nextAt = Date.now() + CrowConfig.firstAfterMs, flapT = 0, landed = null, suspended = false // suspended: dev/test — no crow comes by itself
  const gap = () => CrowConfig.everyMinMs + Math.random() * (CrowConfig.everyMaxMs - CrowConfig.everyMinMs)

  function spawn() {
    if (state !== 'idle') return false
    row = rice.fullestRow(CrowConfig.minRowProgress); if (!row) return false
    const z = row.z0 + 0.15 * (row.z1 - row.z0) + Math.random() * 0.7 * (row.z1 - row.z0)
    const side = row.x >= 0 ? 1 : -1
    landed = { x: row.x, y: row.y + 0.01, z }
    const sx = row.x + side * 3.2, sz = z + (Math.random() - 0.5) * 1.5
    shadow.position.set(sx, 0.02, sz); shadow.visible = true; shadow.scale.setScalar(1.6)
    crow.position.set(sx, landed.y + 2.2, sz); crow.rotation.y = Math.atan2(landed.x - sx, landed.z - sz); crow.visible = true
    state = 'sweep'
    const d = CrowConfig.sweepMs / 1000
    gsap.to(shadow.position, { x: landed.x, z: landed.z, duration: d, ease: 'power1.inOut' })
    gsap.to(shadow.scale, { x: 1, y: 1, z: 1, duration: d, ease: 'power1.in' })
    gsap.to(crow.position, { x: landed.x, z: landed.z, duration: d, ease: 'power1.inOut' })
    gsap.to(crow.position, { y: landed.y, duration: d, ease: 'power2.in', onComplete: () => { if (state !== 'sweep') return; state = 'sitting'; sitUntil = Date.now() + CrowConfig.sitMs; shadow.visible = false; emit(Events.CrowLand, { k: row.k, key: row.key, ri: row.ri }) } })
    emit(Events.CrowSweep, { k: row.k })
    return true
  }
  function leave(why) {
    if (state === 'idle' || state === 'leaving') return
    state = 'leaving'; shadow.visible = false
    gsap.killTweensOf(crow.position); gsap.killTweensOf(shadow.position); gsap.killTweensOf(shadow.scale)
    const side = crow.position.x >= 0 ? 1 : -1
    gsap.to(crow.position, { x: crow.position.x + side * 2.6, z: crow.position.z - 0.6, y: crow.position.y + 2.4, duration: 1.3, ease: 'power2.in', onComplete: () => { crow.visible = false; state = 'idle'; row = null; nextAt = Date.now() + gap() } })
    crow.userData.leftWhy = why
  }
  // The player clicked it: feathers, and off it goes. True when there was a crow to shoo.
  function shoo() {
    if (state !== 'sitting' && state !== 'sweep') return false
    const p = crow.position
    fx.specks(p.x, p.y + 0.08, p.z, 10, FEATHER, 0.7, 0.05); fx.specks(p.x, p.y + 0.12, p.z, 8, FEATHER, 0.9, 0.04)
    emit(Events.CrowShooed, { k: row?.k ?? null })
    leave('shooed')
    return true
  }
  function eat() {
    if (state !== 'sitting') return
    const lost = season.loseClicks(CrowConfig.eatsClicks, 'crow')
    fx.burst(crow.position.x, crow.position.y, crow.position.z, 8, [0.85, 0.7, 0.35])
    emit(Events.CrowAte, { k: row?.k ?? null, lost })
    leave('ate')
  }
  function tick(dt) {
    const now = Date.now()
    if (state === 'idle') { if (!suspended && now >= nextAt && canSpawn()) spawn(); return }
    const parts = crow.userData.parts
    if (state === 'sweep' || state === 'leaving') { flapT += dt * 22; const a = Math.sin(flapT) * 0.75; parts.wingL.rotation.z = 0.2 + a; parts.wingR.rotation.z = -0.2 - a }
    else if (state === 'sitting') {
      parts.wingL.rotation.z = 0.15; parts.wingR.rotation.z = -0.15
      parts.head.position.y = parts.headY - Math.max(0, Math.sin(now / 1000 * 7)) * 0.02 // pecking
      if (now >= sitUntil) eat()
    }
  }
  const api = {
    tick, spawn, shoo, mesh: crow,
    eatNow: () => { if (state === 'sitting') sitUntil = 0 }, // (the Testing Field: "what if it is left")
    pick: () => (state === 'sitting' || state === 'sweep' ? [crow] : []),
    state: () => ({ state, k: row?.k ?? null, x: +crow.position.x.toFixed(3), z: +crow.position.z.toFixed(3), sitLeftMs: state === 'sitting' ? Math.max(0, sitUntil - Date.now()) : null, nextInMs: state === 'idle' ? Math.max(0, nextAt - Date.now()) : null }),
  }
  if (import.meta.env?.DEV) window.__crow = { ...api, warp: (ms) => { sitUntil -= ms; nextAt -= ms }, suspend: (on) => { suspended = !!on }, cfg: CrowConfig }
  return api
}
